import { schemas } from '@repo/contracts'
import { z } from 'zod'
import { parseDataOfType } from '@/common/parseDataType'
import { apiErrorHandler } from '../errorHandler'
import {
  conversationBriefSourceSchema as briefSourceResponseSchema,
  conversationBriefValueResponseSchema as briefValueResponseSchema,
  conversationBriefValueSchema as briefValueSchema,
  parseCanonicalConversationJSON
} from './canonical'
import { conversationSnapshotToFrame } from './stream'

export type ConversationSnapshot = z.infer<typeof schemas.apihttp_ConversationSnapshotResponse>
export type ConversationCommand = z.infer<typeof schemas.apihttp_ConversationCommandRequest>
export type ConversationCommandResponse = z.infer<typeof schemas.apihttp_ConversationCommandResponse>
export type ConversationBrief = z.infer<typeof schemas.apihttp_ConversationBriefResponse>
export type ConversationBriefCorrection = z.infer<typeof schemas.apihttp_ConversationBriefCorrectionRequest>
export type ConversationExecutionRequest = z.infer<typeof schemas.apihttp_ConversationExecutionRequest>
export type ConversationExecution = z.infer<typeof schemas.apihttp_ConversationExecutionResponse>
export type ConversationRun = z.infer<typeof schemas.apihttp_ConversationRunResponse>
export type ConversationQuestionRun = z.infer<typeof schemas.apihttp_ConversationQuestionRunResponse>
export type ConversationStaleness = z.infer<typeof schemas.apihttp_ConversationStalenessResponse>
export type ConversationRedraftRequest = z.infer<typeof schemas.apihttp_ConversationRedraftRequest>
export type ConversationMessage = z.infer<typeof schemas.apihttp_ConversationSnapshotMessageResponse>
export type ConversationComputedResult = z.infer<typeof schemas.apihttp_ConversationComputedResultResponse>
export type ConversationQuestion = z.infer<typeof schemas.apihttp_ConversationQuestionResponse>
export type ConversationShell = z.infer<typeof schemas.apihttp_ConversationShellResponse>
export type ConversationChat = z.infer<typeof schemas.apihttp_ConversationChatResponse>
export type ConversationChatPage = z.infer<typeof schemas.apihttp_ConversationChatPageResponse>
export type ConversationCapability = z.infer<typeof schemas.apihttp_ConversationCapabilityResponse>
export type ConversationLegacyRoute = z.infer<typeof schemas.apihttp_ConversationLegacyRouteResponse>

export type ConversationShellOptions = {
  before?: string
  expandedProjectId?: number
  archivedProjectId?: number
  archivedBefore?: string
  signal?: AbortSignal
}

export type ConversationChatListOptions = {
  before?: string
  limit?: number
  archived?: boolean
  signal?: AbortSignal
}

export type ConversationRenameChatInput = {
  title: string
  expectedRevisionTsUs: number
}

type SnapshotOptions = {
  before?: string
  limit?: number
  signal?: AbortSignal
}

const safePositiveInteger = z.number().int().positive().max(Number.MAX_SAFE_INTEGER)
const nonnegativeSafeInteger = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
const safeInteger = z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER)
const boundedString = (maximum: number) =>
  z.string().refine((value) => new TextEncoder().encode(value).byteLength <= maximum)
const containsControlCharacter = (value: string) => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0
    if (codePoint <= 31 || codePoint === 127) return true
  }
  return false
}
const cursor = boundedString(2048).refine((value) => !containsControlCharacter(value))
const requestCursor = cursor.min(1)
const chatTitle = z
  .string()
  .min(1)
  .refine(
    (value) =>
      value.trim() !== '' && new TextEncoder().encode(value).byteLength <= 160 && !containsControlCharacter(value)
  )
const nonNilUUID = z
  .string()
  .uuid()
  .refine((value) => value !== '00000000-0000-0000-0000-000000000000')
const slotKey = z
  .string()
  .min(1)
  .max(128)
  .refine((value) => value.trim() === value)
const byteBoundedText = (maximum: number) =>
  z
    .string()
    .min(1)
    .refine((value) => value.trim() !== '' && new TextEncoder().encode(value).byteLength <= maximum)
const uniquePositiveIDs = (minimum: number) =>
  z
    .array(safePositiveInteger)
    .min(minimum)
    .max(32)
    .refine((values) => new Set(values).size === values.length)
const textAnswerSchema = schemas.apihttp_ConversationTextAnswerRequest
  .extend({ content: byteBoundedText(32 * 1024) })
  .strict()
const integerAnswerSchema = schemas.apihttp_ConversationIntegerAnswerRequest.extend({ content: safeInteger }).strict()
const documentsAnswerSchema = schemas.apihttp_ConversationDocumentsAnswerRequest
  .extend({ selectedIds: uniquePositiveIDs(1) })
  .strict()
const proposalSectionAnswerSchema = schemas.apihttp_ConversationProposalSectionAnswerRequest
  .extend({
    proposalId: safePositiveInteger,
    sectionId: safePositiveInteger,
    observation: z
      .string()
      .min(1)
      .refine((value) => value.trim() === value && new TextEncoder().encode(value).byteLength <= 2048)
  })
  .strict()
// The whole-proposal branch of the artifact union: a proposal names no section,
// and any positive sectionId is refused by the server at decode time, so the
// client mirror accepts only an absent or zero one.
const proposalAnswerSchema = schemas.apihttp_ConversationProposalAnswerRequest
  .extend({
    proposalId: safePositiveInteger,
    sectionId: z.literal(0).optional(),
    observation: z
      .string()
      .min(1)
      .refine((value) => value.trim() === value && new TextEncoder().encode(value).byteLength <= 2048)
  })
  .strict()
const skipAnswerSchema = schemas.apihttp_ConversationSkipAnswerRequest.strict()
const answerSchema = z.discriminatedUnion('type', [
  textAnswerSchema,
  integerAnswerSchema,
  documentsAnswerSchema,
  proposalSectionAnswerSchema,
  proposalAnswerSchema,
  skipAnswerSchema
])
const taskCommandSchema = schemas.apihttp_ConversationTaskCommandRequest
  .extend({
    clientTurnId: nonNilUUID,
    content: byteBoundedText(32 * 1024),
    tagIds: uniquePositiveIDs(0),
    attachedDocumentIds: uniquePositiveIDs(0)
  })
  .strict()
const answerCommandSchema = schemas.apihttp_ConversationAnswerCommandRequest
  .extend({
    clientTurnId: nonNilUUID,
    briefId: safePositiveInteger,
    runId: safePositiveInteger,
    suspensionKey: byteBoundedText(256),
    answer: answerSchema
  })
  .strict()
const commandRequestSchema = z.discriminatedUnion('type', [taskCommandSchema, answerCommandSchema])
const briefCorrectionSlotSchema = schemas.apihttp_ConversationBriefCorrectionSlotRequest
  .extend({ key: slotKey, value: briefValueSchema })
  .strict()
const briefCorrectionRequestSchema = schemas.apihttp_ConversationBriefCorrectionRequest
  .extend({
    clientTurnId: nonNilUUID,
    expectedVersion: safePositiveInteger,
    content: byteBoundedText(32 * 1024),
    set: z
      .array(briefCorrectionSlotSchema)
      .max(64)
      .refine((slots) => new Set(slots.map((slot) => slot.key)).size === slots.length),
    clear: z
      .array(slotKey)
      .max(64)
      .refine((keys) => new Set(keys).size === keys.length)
  })
  .strict()
  .superRefine((correction, context) => {
    const setKeys = new Set(correction.set.map((slot) => slot.key))
    if (correction.clear.some((key) => setKeys.has(key))) {
      context.addIssue({ code: 'custom', message: 'set and clear slot keys must be disjoint' })
    }
  })
const executionRequestSchema = schemas.apihttp_ConversationExecutionRequest
  .extend({ clientRequestId: nonNilUUID, expectedVersion: safePositiveInteger })
  .strict()
const redraftRequestSchema = schemas.apihttp_ConversationRedraftRequest
  .extend({ clientRequestId: nonNilUUID, briefId: safePositiveInteger, expectedVersion: safePositiveInteger })
  .strict()

// Everything above this line is sent and stays `.strict()`: we build those bodies from typed local
// code, so an unknown key is our own bug. Everything below is received and `.strip()`s instead. The
// generated base name carries the split, `apihttp_*Request` against `apihttp_*Response`, and
// ADR-176 records why: `contracts:check` already proves the schema matches the Go structs before
// anything ships, so runtime key-rejection catches no drift a build gate has not caught, and costs
// the whole response the first time an old bundle meets a newer server.
//
// `.strip()` rather than the generated `.passthrough()` default: `requestJSON` walks the parsed
// result with `assertSafeIntegers`, so an unfamiliar key carrying an int64-shaped number would
// throw away the response it was supposed to survive.
const capabilityResponseSchema = schemas.apihttp_ConversationCapabilityResponse
  .extend({ suggestions: z.array(z.string()) })
  .strip()
const capabilityListResponseSchema = schemas.apihttp_ConversationCapabilityListResponse
  .extend({ items: z.array(capabilityResponseSchema) })
  .strip()

const shellCreatorSchema = schemas.apihttp_ConversationShellCreatorResponse
  .extend({ userId: safePositiveInteger, displayName: boundedString(512) })
  .strip()
const shellCountSchema = schemas.apihttp_ConversationShellCountResponse
  .extend({ count: nonnegativeSafeInteger })
  .strip()
const shellCountsSchema = schemas.apihttp_ConversationShellCountsResponse
  .extend({
    proposals: shellCountSchema,
    presentations: shellCountSchema,
    content: shellCountSchema,
    connectors: shellCountSchema
  })
  .strip()
const shellChatSchema = schemas.apihttp_ConversationShellChatResponse
  .extend({
    projectId: safePositiveInteger,
    conversationId: safePositiveInteger,
    title: chatTitle,
    activityAtTsMs: safePositiveInteger,
    archivedAtTsMs: safePositiveInteger.optional(),
    creator: shellCreatorSchema
  })
  .strip()
  .superRefine((chat, context) => {
    if (chat.originKind === 'native' && chat.readOnly) {
      context.addIssue({ code: 'custom', message: 'native shell chat cannot be cutover read-only' })
    }
  })
const shellChatPageSchema = schemas.apihttp_ConversationShellChatPageResponse
  .extend({ items: z.array(shellChatSchema).max(8), cursor })
  .strip()
  .superRefine((page, context) => {
    if (page.hasMore !== (page.cursor !== '')) {
      context.addIssue({ code: 'custom', message: 'chat page cursor does not match hasMore' })
    }
    if (new Set(page.items.map((item) => item.conversationId)).size !== page.items.length) {
      context.addIssue({ code: 'custom', message: 'chat page contains duplicate conversation IDs' })
    }
  })
const shellPreviousWorkSchema = schemas.apihttp_ConversationShellPreviousWorkResponse
  .extend({
    projectId: safePositiveInteger,
    threadId: safePositiveInteger,
    title: boundedString(512),
    updatedAtTsMs: safePositiveInteger,
    creator: shellCreatorSchema
  })
  .strip()
const shellPreviousWorkPageSchema = schemas.apihttp_ConversationShellPreviousWorkPageResponse
  .extend({ items: z.array(shellPreviousWorkSchema).max(6), cursor })
  .strip()
  .refine((page) => page.hasMore === (page.cursor !== ''), 'previous-work cursor does not match hasMore')
const shellWorkspaceSchema = schemas.apihttp_ConversationShellWorkspaceResponse
  .extend({
    projectId: safePositiveInteger,
    title: boundedString(512),
    memberCount: safePositiveInteger,
    activityAtTsMs: safePositiveInteger,
    creator: shellCreatorSchema,
    counts: shellCountsSchema,
    chats: shellChatPageSchema,
    previousWork: shellPreviousWorkPageSchema
  })
  .strip()
// Nested generated schemas have to be re-declared to strip: `.strip()` binds the object it is
// called on, not the ones it references, and everything reached only through a reference stays on
// the generated `.passthrough()`. That matters because `assertSafeIntegers` walks whatever survives
// the parse, so a carried key deep in the tree fails the response just as surely as a top-level one.
const shellArtifactOriginSchema = schemas.apihttp_ConversationShellArtifactOriginResponse
  .extend({ conversationId: safePositiveInteger, title: boundedString(512) })
  .strip()
const shellArtifactSchema = schemas.apihttp_ConversationShellArtifactResponse
  .extend({
    id: safePositiveInteger,
    status: nonnegativeSafeInteger,
    title: boundedString(512),
    updatedAtTsMs: safePositiveInteger,
    creator: shellCreatorSchema,
    origin: shellArtifactOriginSchema.optional()
  })
  .strip()
const shellArtifactPageSchema = schemas.apihttp_ConversationShellArtifactPageResponse
  // No cursor: the artifact list is a bounded recent list, and the parameter that would
  // page it does not exist. It carried one that no request could send.
  //
  // A row of a kind this client does not know is dropped rather than failing the page.
  // The server gains artifact kinds ahead of the clients that render them, and a strict
  // parse would turn one new row into an empty rail for everything else. This is the
  // deployment form of the M9.14 lesson recorded in BUG-397.
  .extend({
    items: z
      .array(z.unknown())
      .max(20)
      .transform((rows) =>
        rows.flatMap((row) => {
          const parsed = shellArtifactSchema.safeParse(row)
          return parsed.success ? [parsed.data] : []
        })
      )
  })
  .strip()
const shellExpandedSchema = schemas.apihttp_ConversationShellExpandedResponse
  .extend({
    projectId: safePositiveInteger,
    artifacts: shellArtifactPageSchema
  })
  .strip()
const shellArchivedSchema = schemas.apihttp_ConversationShellArchivedResponse
  .extend({ projectId: safePositiveInteger, items: z.array(shellChatSchema).max(20), cursor })
  .strip()
  .refine((page) => page.hasMore === (page.cursor !== ''), 'archive cursor does not match hasMore')
const shellOrganisationSchema = schemas.apihttp_ConversationShellOrganisationResponse
  .extend({ orgId: safePositiveInteger, name: boundedString(512), slug: boundedString(256), counts: shellCountsSchema })
  .strip()
const shellResponseSchema = schemas.apihttp_ConversationShellResponse
  .extend({
    organisation: shellOrganisationSchema,
    workspaces: z.array(shellWorkspaceSchema).max(50),
    workspaceCursor: cursor,
    expanded: shellExpandedSchema.optional(),
    archived: shellArchivedSchema.optional()
  })
  .strip()
  .superRefine((shell, context) => {
    if (shell.workspacesHasMore !== (shell.workspaceCursor !== '')) {
      context.addIssue({ code: 'custom', message: 'workspace cursor does not match hasMore' })
    }
    const projectIDs = shell.workspaces.map((workspace) => workspace.projectId)
    if (new Set(projectIDs).size !== projectIDs.length) {
      context.addIssue({ code: 'custom', message: 'shell contains duplicate workspace IDs' })
    }
    for (const workspace of shell.workspaces) {
      if (
        workspace.chats.items.some((chat) => chat.projectId !== workspace.projectId) ||
        workspace.previousWork.items.some((item) => item.projectId !== workspace.projectId)
      ) {
        context.addIssue({ code: 'custom', message: 'workspace child project identity does not match' })
      }
    }
    if (shell.archived?.items.some((chat) => chat.projectId !== shell.archived?.projectId)) {
      context.addIssue({ code: 'custom', message: 'archived chat project identity does not match' })
    }
  })

const createChatRequestSchema = schemas.apihttp_ConversationCreateChatRequest
  .extend({ clientRequestId: nonNilUUID })
  .strict()
const renameChatRequestSchema = schemas.apihttp_ConversationRenameChatRequest
  .extend({ title: chatTitle, expectedRevisionTsUs: safePositiveInteger })
  .strict()
const chatObservationRequestSchema = schemas.apihttp_ConversationChatObservationRequest
  .extend({ expectedRevisionTsUs: safePositiveInteger })
  .strict()
const chatResponseSchema = schemas.apihttp_ConversationChatResponse
  .extend({
    conversationId: safePositiveInteger,
    clientRequestId: nonNilUUID.optional(),
    title: chatTitle,
    activityAtTsMs: safePositiveInteger,
    archivedAtTsMs: safePositiveInteger.optional(),
    archivedBy: safePositiveInteger.optional(),
    createdBy: safePositiveInteger,
    updatedAtTsMs: safePositiveInteger,
    revisionTsUs: safePositiveInteger
  })
  .strip()
  .superRefine((chat, context) => {
    if ((chat.archivedAtTsMs === undefined) !== (chat.archivedBy === undefined)) {
      context.addIssue({ code: 'custom', message: 'chat archive observation is incomplete' })
    }
    if (chat.originKind === 'native' && chat.readOnly) {
      context.addIssue({ code: 'custom', message: 'native chat cannot be cutover read-only' })
    }
  })
const legacyRouteResponseSchema = schemas.apihttp_ConversationLegacyRouteResponse
  .extend({ threadId: safePositiveInteger, conversationId: safePositiveInteger.optional() })
  .strip()
  .superRefine((resolution, context) => {
    if ((resolution.disposition === 'redirect') !== (resolution.conversationId !== undefined)) {
      context.addIssue({ code: 'custom', message: 'legacy redirect identity is incomplete' })
    }
    if (resolution.conversationId !== undefined && resolution.conversationId !== resolution.threadId) {
      context.addIssue({ code: 'custom', message: 'legacy redirect identity changed' })
    }
  })
const chatPageResponseSchema = schemas.apihttp_ConversationChatPageResponse
  .extend({ items: z.array(chatResponseSchema).max(50), cursor })
  .strip()
  .superRefine((page, context) => {
    if (page.hasMore !== (page.cursor !== ''))
      context.addIssue({ code: 'custom', message: 'chat cursor does not match hasMore' })
    if (new Set(page.items.map((item) => item.conversationId)).size !== page.items.length) {
      context.addIssue({ code: 'custom', message: 'chat page contains duplicate conversation IDs' })
    }
  })
const chatConflictResponseSchema = schemas.apihttp_ConversationChatConflictResponse
  .extend({ current: chatResponseSchema })
  .strip()

const briefSlotResponseSchema = schemas.apihttp_ConversationBriefSlotResponse
  .extend({ key: slotKey, source: briefSourceResponseSchema.optional(), value: briefValueResponseSchema })
  .strip()
// Re-declared rather than inherited: `.strip()` binds the object it is called on and
// not the schemas that object references, so a nested generated schema left alone keeps
// its `.passthrough()` default and carries unknown keys into `assertSafeIntegers`.
const briefMissingSlotResponseSchema = schemas.apihttp_ConversationBriefMissingSlotResponse
  .extend({ key: slotKey, label: boundedString(256), answerStem: boundedString(256) })
  .strip()
const briefResponseSchema = schemas.apihttp_ConversationBriefResponse
  .extend({
    briefId: safePositiveInteger,
    definitionVersion: safePositiveInteger,
    version: safePositiveInteger,
    slots: z.array(briefSlotResponseSchema),
    missingSlotKeys: z.array(slotKey),
    missingSlots: z.array(briefMissingSlotResponseSchema)
  })
  .strip()
const computedExcludedCellResponseSchema = schemas.apihttp_ConversationComputedExcludedCellResponse
  .extend({ count: nonnegativeSafeInteger })
  .strip()
const computedTableResponseSchema = schemas.apihttp_ConversationComputedTableResponse
  .extend({
    cellsComputed: nonnegativeSafeInteger,
    rowsMatched: nonnegativeSafeInteger,
    rowsTotal: nonnegativeSafeInteger,
    excludedCells: z.array(computedExcludedCellResponseSchema)
  })
  .strip()
const computedResultResponseSchema = schemas.apihttp_ConversationComputedResultResponse
  .extend({
    sourceDocumentIds: z.array(safePositiveInteger),
    table: computedTableResponseSchema.nullish()
  })
  .strip()
const snapshotMessageResponseSchema = schemas.apihttp_ConversationSnapshotMessageResponse
  .extend({
    messageId: safePositiveInteger,
    createdBy: safePositiveInteger,
    createdAtTsMs: safePositiveInteger,
    updatedAtTsMs: safePositiveInteger,
    computedResults: z.array(computedResultResponseSchema).optional(),
    proposalId: safePositiveInteger.nullish(),
    proposalSectionId: safePositiveInteger.nullish(),
    presentationId: safePositiveInteger.nullish(),
    presentationSlideId: safePositiveInteger.nullish(),
    presentationSlideVersionId: safePositiveInteger.nullish()
  })
  .strip()
const questionCandidateResponseSchema = schemas.apihttp_ConversationQuestionCandidateResponse
  .extend({ id: safePositiveInteger })
  .strip()
const artifactCandidateResponseSchema = schemas.apihttp_ConversationProposalSectionCandidateResponse
  .extend({
    // Defaulted although the generated contract requires it: the same tolerance
    // the stream and canonical brief-value schemas carry, so one live surface
    // cannot reject what its siblings accept.
    type: z.enum(['proposal-section', 'proposal']).default('proposal-section'),
    proposalId: safePositiveInteger,
    sectionId: nonnegativeSafeInteger
  })
  .strip()
  .superRefine((candidate, context) => {
    // A whole-proposal candidate names no section; a section candidate keeps a
    // positive id.
    if (candidate.type === 'proposal' && candidate.sectionId !== 0) {
      context.addIssue({ code: 'custom', path: ['sectionId'], message: 'a whole-proposal candidate names no section' })
    }
    if (candidate.type === 'proposal-section' && candidate.sectionId <= 0) {
      context.addIssue({ code: 'custom', path: ['sectionId'], message: 'a section candidate needs its section' })
    }
  })
const questionSatisfiedSlotResponseSchema = schemas.apihttp_ConversationQuestionSatisfiedSlotResponse
  .extend({ key: slotKey, label: boundedString(256), summary: boundedString(1024) })
  .strip()
const questionResponseSchema = schemas.apihttp_ConversationQuestionResponse
  .extend({
    briefId: safePositiveInteger,
    briefVersion: safePositiveInteger,
    runId: safePositiveInteger,
    ordinal: safePositiveInteger,
    candidates: z.array(questionCandidateResponseSchema),
    artifactOptions: z.array(artifactCandidateResponseSchema),
    satisfied: z.array(questionSatisfiedSlotResponseSchema),
    skippable: z.boolean().default(false)
  })
  .strip()
const taskResponseSchema = schemas.apihttp_ConversationSnapshotTaskResponse
  .extend({
    originMessageId: safePositiveInteger,
    intakeRunId: safePositiveInteger,
    briefId: safePositiveInteger.nullish(),
    definitionVersion: safePositiveInteger.nullish(),
    currentBriefVersion: safePositiveInteger.nullish(),
    questionRunId: safePositiveInteger.optional(),
    executionId: safePositiveInteger.nullish(),
    executionRunId: safePositiveInteger.optional(),
    executionSourceMessageId: safePositiveInteger.nullable(),
    executionOutputMessageIds: z
      .array(safePositiveInteger)
      .max(200)
      .refine((ids) => new Set(ids).size === ids.length, 'execution output message IDs must be unique')
  })
  .strip()
const stalenessResponseSchema = schemas.apihttp_ConversationStalenessResponse
  .extend({
    messageId: safePositiveInteger,
    briefId: safePositiveInteger.nullish(),
    linkedVersion: safePositiveInteger.nullish(),
    currentVersion: safePositiveInteger.nullish()
  })
  .strip()
const snapshotResponseSchema = schemas.apihttp_ConversationSnapshotResponse
  .extend({
    threadId: safePositiveInteger,
    highWaterEventId: nonnegativeSafeInteger,
    tailAfterEventId: nonnegativeSafeInteger,
    messages: z.array(snapshotMessageResponseSchema),
    tasks: z.array(taskResponseSchema),
    briefs: z.array(briefResponseSchema),
    questions: z.array(questionResponseSchema),
    outputs: z.array(stalenessResponseSchema)
  })
  .strip()
  .superRefine((snapshot, context) => {
    if (snapshot.originKind === 'native' && snapshot.readOnly) {
      context.addIssue({ code: 'custom', message: 'native snapshot cannot be cutover read-only' })
    }
  })
const commandResponseSchema = schemas.apihttp_ConversationCommandResponse
  .extend({
    threadId: safePositiveInteger,
    messageId: safePositiveInteger,
    runId: safePositiveInteger,
    briefId: safePositiveInteger.nullish(),
    clientTurnId: nonNilUUID
  })
  .strip()
const executionResponseSchema = schemas.apihttp_ConversationExecutionResponse
  .extend({
    threadId: safePositiveInteger,
    briefId: safePositiveInteger,
    briefVersion: safePositiveInteger,
    briefVersionId: safePositiveInteger,
    executionId: safePositiveInteger,
    runId: safePositiveInteger
  })
  .strip()
const runResponseSchema = schemas.apihttp_ConversationRunResponse
  .extend({ executionId: safePositiveInteger, runId: safePositiveInteger })
  .strip()

const questionRunResponseSchema = schemas.apihttp_ConversationQuestionRunResponse
  .extend({ briefId: safePositiveInteger, runId: safePositiveInteger })
  .strip()

function positiveId(value: number, name: string): number {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error(`${name} must be a positive integer`)
  return value
}

// Two regions of the snapshot are free-form on the server: `refs` is a `json.RawMessage` and
// `events` is a `[]map[string]any`. No schema here can enumerate their keys, so unlike every other
// nested object they cannot be bounded by re-declaring them with `.strip()`. The walk below skips
// them rather than failing the whole response over a number the client never reads as an identity.
// They are not unchecked: each embedded event is decoded by `decodeConversationFrame`, which bounds
// every number it does read. See ADR-176.
const opaqueSnapshotRegions = new Set(['refs', 'events'])

function assertSafeIntegers(value: unknown): void {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || (Number.isInteger(value) && !Number.isSafeInteger(value))) {
      throw new Error('conversation payload contains an unsafe number')
    }
    return
  }
  if (Array.isArray(value)) {
    for (const item of value) assertSafeIntegers(item)
    return
  }
  if (typeof value === 'object' && value !== null) {
    for (const [key, item] of Object.entries(value)) {
      if (opaqueSnapshotRegions.has(key)) continue
      assertSafeIntegers(item)
    }
  }
}

async function requestJSON<T extends z.ZodTypeAny>(path: string, init: RequestInit, schema: T): Promise<z.infer<T>> {
  const response = await fetch(path, init)
  await apiErrorHandler(response)
  const result = parseDataOfType(parseCanonicalConversationJSON(await response.text(), 1024 * 1024), schema)
  assertSafeIntegers(result)
  return result
}

function jsonRequest(body: unknown, method: 'PATCH' | 'POST'): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body)
  }
}

function querySuffix(query: URLSearchParams): string {
  return query.size === 0 ? '' : `?${query.toString()}`
}

// Every conversation route names its chat. The chat-less form this used to fall back to
// was the pre-M9.10 single-conversation shape, and it is retired: see the design at
// docs/specs/2026-08-15-conversation-contract-coverage.md.
function conversationPath(projectId: number, conversationId: number): string {
  positiveId(projectId, 'projectId')
  positiveId(conversationId, 'conversationId')
  return `/api/projects/${projectId}/conversations/${conversationId}`
}

function requireConversationIdentity<T extends { conversationId: number }>(value: T, expected: number): T {
  if (value.conversationId !== expected) throw new Error('conversation response does not match the requested chat')
  return value
}

function requireThreadIdentity<T extends { threadId: number }>(value: T, expected: number): T {
  if (value.threadId !== expected) throw new Error('conversation response does not match the requested chat')
  return value
}

export class ConversationChatConflictError extends Error {
  readonly current: ConversationChat

  constructor(current: ConversationChat) {
    super('conversation chat changed before the rename was applied')
    this.name = 'ConversationChatConflictError'
    this.current = current
  }
}

export async function getConversationShell(
  orgId: number,
  options: ConversationShellOptions = {}
): Promise<ConversationShell> {
  positiveId(orgId, 'orgId')
  if (options.archivedBefore !== undefined && options.archivedProjectId === undefined) {
    throw new Error('archivedBefore requires archivedProjectId')
  }
  const query = new URLSearchParams()
  if (options.before !== undefined) query.set('before', requestCursor.parse(options.before))
  if (options.expandedProjectId !== undefined) {
    query.set('expandedProjectId', String(positiveId(options.expandedProjectId, 'expandedProjectId')))
  }
  if (options.archivedProjectId !== undefined) {
    query.set('archivedProjectId', String(positiveId(options.archivedProjectId, 'archivedProjectId')))
  }
  if (options.archivedBefore !== undefined) query.set('archivedBefore', requestCursor.parse(options.archivedBefore))
  const shell = await requestJSON(
    `/api/organisations/${orgId}/conversation-shell${querySuffix(query)}`,
    {
      method: 'GET',
      credentials: 'same-origin',
      ...(options.signal === undefined ? {} : { signal: options.signal })
    },
    shellResponseSchema
  )
  if (shell.organisation.orgId !== orgId) throw new Error('conversation shell organisation does not match the request')
  if ((options.expandedProjectId === undefined) !== (shell.expanded === undefined)) {
    throw new Error('expanded workspace presence does not match the request')
  }
  if (options.expandedProjectId !== undefined && shell.expanded?.projectId !== options.expandedProjectId) {
    throw new Error('expanded workspace does not match the request')
  }
  if ((options.archivedProjectId === undefined) !== (shell.archived === undefined)) {
    throw new Error('archive workspace presence does not match the request')
  }
  if (options.archivedProjectId !== undefined && shell.archived?.projectId !== options.archivedProjectId) {
    throw new Error('archive workspace does not match the request')
  }
  return shell
}

// The capability read is workspace-scoped and has no chat: it describes what can be asked
// for here, not what any conversation is doing. Nothing from it is sent back on a task, so
// a phrase the user edits or ignores costs nothing.
export async function listConversationCapabilities(
  projectId: number,
  options: { signal?: AbortSignal } = {}
): Promise<Array<ConversationCapability>> {
  positiveId(projectId, 'projectId')
  const response = await fetch(`/api/projects/${projectId}/conversation-capabilities`, {
    method: 'GET',
    credentials: 'same-origin',
    ...(options.signal === undefined ? {} : { signal: options.signal })
  })
  // A workspace that grants no capability answers 404, which is a normal state here rather
  // than a fault: the reader gets a composer that narrows to nothing. Routing it through
  // apiErrorHandler would report an exception to Sentry on every such workspace open, and
  // the reader would see no symptom to match it against. getConversationLegacyRoute treats
  // its own expected absence the same way.
  if (response.status === 404) {
    // Nothing reads this body, and an unread one holds its stream open.
    await response.body?.cancel()
    return []
  }
  await apiErrorHandler(response)
  const page = parseDataOfType(
    parseCanonicalConversationJSON(await response.text(), 1024 * 1024),
    capabilityListResponseSchema
  )
  return page.items
}

export async function createConversationChat(projectId: number, clientRequestId: string): Promise<ConversationChat> {
  positiveId(projectId, 'projectId')
  const body = parseDataOfType({ clientRequestId }, createChatRequestSchema)
  return requestJSON(`/api/projects/${projectId}/conversations`, jsonRequest(body, 'POST'), chatResponseSchema)
}

export async function listConversationChats(
  projectId: number,
  options: ConversationChatListOptions = {}
): Promise<ConversationChatPage> {
  positiveId(projectId, 'projectId')
  const query = new URLSearchParams()
  if (options.before !== undefined) query.set('before', requestCursor.parse(options.before))
  if (options.limit !== undefined) {
    if (!Number.isSafeInteger(options.limit) || options.limit < 1 || options.limit > 50) {
      throw new Error('limit must be an integer from 1 to 50')
    }
    query.set('limit', String(options.limit))
  }
  if (options.archived !== undefined) query.set('archived', String(options.archived))
  const page = await requestJSON(
    `/api/projects/${projectId}/conversations${querySuffix(query)}`,
    {
      method: 'GET',
      credentials: 'same-origin',
      ...(options.signal === undefined ? {} : { signal: options.signal })
    },
    chatPageResponseSchema
  )
  if (options.archived === true && page.items.some((chat) => chat.archivedAtTsMs === undefined)) {
    throw new Error('archived chat page contains an active chat')
  }
  if (options.archived !== true && page.items.some((chat) => chat.archivedAtTsMs !== undefined)) {
    throw new Error('recent chat page contains an archived chat')
  }
  return page
}

export async function getConversationChat(projectId: number, conversationId: number): Promise<ConversationChat> {
  positiveId(projectId, 'projectId')
  positiveId(conversationId, 'conversationId')
  const result = await requestJSON(
    `/api/projects/${projectId}/conversations/${conversationId}/metadata`,
    { method: 'GET', credentials: 'same-origin' },
    chatResponseSchema
  )
  return requireConversationIdentity(result, conversationId)
}

export async function getConversationLegacyRoute(
  projectId: number,
  threadId: number
): Promise<ConversationLegacyRoute | undefined> {
  positiveId(projectId, 'projectId')
  positiveId(threadId, 'threadId')
  const response = await fetch(`/api/projects/${projectId}/legacy-conversations/${threadId}/route`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  if (response.status === 404) return undefined
  await apiErrorHandler(response)
  const result = parseDataOfType(
    parseCanonicalConversationJSON(await response.text(), 1024 * 1024),
    legacyRouteResponseSchema
  )
  assertSafeIntegers(result)
  if (result.threadId !== threadId) throw new Error('legacy route response does not match the requested thread')
  return result
}

export async function renameConversationChat(
  projectId: number,
  conversationId: number,
  input: ConversationRenameChatInput
): Promise<ConversationChat> {
  positiveId(projectId, 'projectId')
  positiveId(conversationId, 'conversationId')
  const body = parseDataOfType(input, renameChatRequestSchema)
  const response = await fetch(
    `/api/projects/${projectId}/conversations/${conversationId}/metadata`,
    jsonRequest(body, 'PATCH')
  )
  if (response.status === 409) {
    const conflict = parseDataOfType(
      parseCanonicalConversationJSON(await response.text(), 1024 * 1024),
      chatConflictResponseSchema
    )
    throw new ConversationChatConflictError(requireConversationIdentity(conflict.current, conversationId))
  }
  await apiErrorHandler(response)
  const result = parseDataOfType(parseCanonicalConversationJSON(await response.text(), 1024 * 1024), chatResponseSchema)
  return requireConversationIdentity(result, conversationId)
}

async function mutateConversationChatArchive(
  projectId: number,
  conversationId: number,
  command: 'archive' | 'restore',
  expectedRevisionTsUs: number
): Promise<ConversationChat> {
  positiveId(projectId, 'projectId')
  positiveId(conversationId, 'conversationId')
  const body = parseDataOfType({ expectedRevisionTsUs }, chatObservationRequestSchema)
  const result = await requestJSON(
    `/api/projects/${projectId}/conversations/${conversationId}/${command}`,
    jsonRequest(body, 'POST'),
    chatResponseSchema
  )
  return requireConversationIdentity(result, conversationId)
}

export async function archiveConversationChat(
  projectId: number,
  conversationId: number,
  expectedRevisionTsUs: number
): Promise<ConversationChat> {
  return mutateConversationChatArchive(projectId, conversationId, 'archive', expectedRevisionTsUs)
}

export async function restoreConversationChat(
  projectId: number,
  conversationId: number,
  expectedRevisionTsUs: number
): Promise<ConversationChat> {
  return mutateConversationChatArchive(projectId, conversationId, 'restore', expectedRevisionTsUs)
}

export async function getConversationChatSnapshot(
  projectId: number,
  conversationId: number,
  options: SnapshotOptions = {}
): Promise<ConversationSnapshot> {
  const path = conversationPath(projectId, conversationId)
  const query = new URLSearchParams()
  if (options.before !== undefined) query.set('before', requestCursor.parse(options.before))
  if (options.limit !== undefined) {
    if (!Number.isSafeInteger(options.limit) || options.limit < 1 || options.limit > 100) {
      throw new Error('limit must be an integer from 1 to 100')
    }
    query.set('limit', String(options.limit))
  }
  const snapshot = await requestJSON(
    `${path}${querySuffix(query)}`,
    {
      method: 'GET',
      credentials: 'same-origin',
      ...(options.signal === undefined ? {} : { signal: options.signal })
    },
    snapshotResponseSchema
  )
  conversationSnapshotToFrame(projectId, snapshot)
  requireThreadIdentity(snapshot, conversationId)
  return snapshot
}

export async function submitConversationChatCommand(
  projectId: number,
  conversationId: number,
  command: ConversationCommand
): Promise<ConversationCommandResponse> {
  const path = conversationPath(projectId, conversationId)
  const body = parseDataOfType(command, commandRequestSchema)
  assertSafeIntegers(body)
  const response = await requestJSON(path, jsonRequest(body, 'POST'), commandResponseSchema)
  requireThreadIdentity(response, conversationId)
  return response
}

export async function getConversationChatBrief(
  projectId: number,
  conversationId: number,
  briefId: number
): Promise<ConversationBrief> {
  const path = conversationPath(projectId, conversationId)
  positiveId(briefId, 'briefId')
  return requestJSON(
    `${path}/briefs/${briefId}`,
    {
      method: 'GET',
      credentials: 'same-origin'
    },
    briefResponseSchema
  )
}

export async function correctConversationChatBrief(
  projectId: number,
  conversationId: number,
  briefId: number,
  correction: ConversationBriefCorrection
): Promise<ConversationBrief> {
  const path = conversationPath(projectId, conversationId)
  positiveId(briefId, 'briefId')
  const body = parseDataOfType(correction, briefCorrectionRequestSchema)
  assertSafeIntegers(body)
  return requestJSON(`${path}/briefs/${briefId}`, jsonRequest(body, 'PATCH'), briefResponseSchema)
}

export async function executeConversationChatBrief(
  projectId: number,
  conversationId: number,
  briefId: number,
  request: ConversationExecutionRequest
): Promise<ConversationExecution> {
  const path = conversationPath(projectId, conversationId)
  positiveId(briefId, 'briefId')
  const body = parseDataOfType(request, executionRequestSchema)
  assertSafeIntegers(body)
  const response = await requestJSON(
    `${path}/briefs/${briefId}/execute`,
    jsonRequest(body, 'POST'),
    executionResponseSchema
  )
  requireThreadIdentity(response, conversationId)
  return response
}

async function executionCommand(
  projectId: number,
  conversationId: number,
  executionId: number,
  command: 'cancel' | 'retry'
): Promise<ConversationRun> {
  const path = conversationPath(projectId, conversationId)
  positiveId(executionId, 'executionId')
  const response = await requestJSON(
    `${path}/executions/${executionId}/${command}`,
    {
      method: 'POST',
      credentials: 'same-origin'
    },
    runResponseSchema
  )
  if (response.executionId !== executionId) throw new Error('conversation run does not match the requested execution')
  return response
}

/**
 * Commands against a brief's question run. Abandoning drops a parked question so the task
 * stops owning the chat; continuing grants a fresh ask budget after exhaustion.
 */
async function questionRunCommand(
  projectId: number,
  conversationId: number,
  briefId: number,
  runId: number,
  command: 'abandon' | 'continue'
): Promise<ConversationQuestionRun> {
  const path = conversationPath(projectId, conversationId)
  positiveId(briefId, 'briefId')
  positiveId(runId, 'runId')
  const response = await requestJSON(
    `${path}/briefs/${briefId}/questions/${runId}/${command}`,
    {
      method: 'POST',
      credentials: 'same-origin'
    },
    questionRunResponseSchema
  )
  if (response.briefId !== briefId || response.runId !== runId) {
    throw new Error('conversation question run does not match the requested brief')
  }
  return response
}

export async function abandonConversationQuestion(
  projectId: number,
  conversationId: number,
  briefId: number,
  runId: number
): Promise<ConversationQuestionRun> {
  return questionRunCommand(projectId, conversationId, briefId, runId, 'abandon')
}

export async function continueConversationQuestions(
  projectId: number,
  conversationId: number,
  briefId: number,
  runId: number
): Promise<ConversationQuestionRun> {
  return questionRunCommand(projectId, conversationId, briefId, runId, 'continue')
}

export async function cancelConversationChatExecution(
  projectId: number,
  conversationId: number,
  executionId: number
): Promise<ConversationRun> {
  return executionCommand(projectId, conversationId, executionId, 'cancel')
}

export async function retryConversationChatExecution(
  projectId: number,
  conversationId: number,
  executionId: number
): Promise<ConversationRun> {
  return executionCommand(projectId, conversationId, executionId, 'retry')
}

export async function getConversationChatOutputStaleness(
  projectId: number,
  conversationId: number,
  messageId: number
): Promise<ConversationStaleness> {
  const path = conversationPath(projectId, conversationId)
  positiveId(messageId, 'messageId')
  const response = await requestJSON(
    `${path}/outputs/${messageId}/staleness`,
    {
      method: 'GET',
      credentials: 'same-origin'
    },
    stalenessResponseSchema
  )
  if (response.messageId !== messageId) throw new Error('staleness response does not match the requested output')
  return response
}

export async function redraftConversationChatOutput(
  projectId: number,
  conversationId: number,
  messageId: number,
  request: ConversationRedraftRequest
): Promise<ConversationExecution> {
  const path = conversationPath(projectId, conversationId)
  positiveId(messageId, 'messageId')
  const body = parseDataOfType(request, redraftRequestSchema)
  assertSafeIntegers(body)
  const response = await requestJSON(
    `${path}/outputs/${messageId}/redraft`,
    jsonRequest(body, 'POST'),
    executionResponseSchema
  )
  requireThreadIdentity(response, conversationId)
  return response
}
